"use client";

import { useLayoutEffect, useRef } from "react";

// ── FLIP: элементы переезжают на новые места, а не подменяются ────────────────
// Один приём на два номера каталога: №41 (переезд строк при сортировке) и
// №33 (соседи расступаются перед перетаскиваемым). Суть: запомнить положение ДО
// перестановки, после перерисовки вернуть элемент на старое место без анимации
// и тут же отпустить — браузер доигрывает переезд сам.
//
// ⚠️ ДВА ОГРАНИЧЕНИЯ, без которых приём вредит:
//   • длинные списки не анимируем совсем — пересчёт геометрии восьмисот строк
//     даёт заметный фриз, а видно от силы двадцать;
//   • анимируем только то, что попало в видимое окно контейнера.

export const FLIP_MAX_ITEMS = 200;
const DURATION_MS = 340;
const EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

export type FlipSnapshot = Map<Element, number>;

/** Запомнить положение элементов перед перестановкой. */
export function captureFlip(items: Iterable<Element>): FlipSnapshot | null {
  const list = Array.from(items);
  if (list.length === 0 || list.length > FLIP_MAX_ITEMS) return null;
  const map: FlipSnapshot = new Map();
  list.forEach((el) => map.set(el, el.getBoundingClientRect().top));
  return map;
}

/** Доиграть переезд после перерисовки. `viewport` — прокручиваемый контейнер. */
export function playFlip(snapshot: FlipSnapshot | null, viewport?: Element | null) {
  if (!snapshot) return;
  if (typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

  const box = viewport?.getBoundingClientRect();
  const moves: { el: HTMLElement; dy: number }[] = [];

  snapshot.forEach((before, node) => {
    const el = node as HTMLElement;
    if (!el.isConnected) return;
    const now = el.getBoundingClientRect();
    if (box && (now.bottom < box.top || now.top > box.bottom)) return;
    const dy = before - now.top;
    if (Math.abs(dy) > 0.5) moves.push({ el, dy });
  });
  if (moves.length === 0) return;

  moves.forEach(({ el, dy }) => {
    el.style.transition = "none";
    el.style.transform = `translateY(${dy}px)`;
  });
  // Два кадра: первый — чтобы браузер принял стартовое положение, второй —
  // чтобы transition увидел смену значения, а не начальную установку.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      moves.forEach(({ el }) => {
        el.style.transition = `transform ${DURATION_MS}ms ${EASING}`;
        el.style.transform = "";
      });
    });
  });
}

/**
 * Автоматический FLIP для списка, порядок которого меняется перетаскиванием
 * (№33 каталога движения). Соседние строки плавно расступаются перед
 * перетаскиваемой, а не перескакивают.
 *
 * Пользоваться так: контейнеру дать ref, элементам — стабильные ключи React, и
 * передать сюда значение, которое меняется при перестановке (например массив
 * идентификаторов в текущем порядке).
 */
export function useFlipList(
  containerRef: { current: HTMLElement | null },
  itemSelector: string,
  order: string,
) {
  const snapshot = useRef<FlipSnapshot | null>(null);
  const prevOrder = useRef(order);

  // Снимок делаем ПЕРЕД перерисовкой: useLayoutEffect отрабатывает уже после,
  // поэтому положение «до» берём из предыдущего прохода — на первом рендере
  // после смены порядка DOM ещё не тронут только в useMemo/render, а здесь
  // достаточно сравнить порядок и снять кадр до применения стилей.
  if (prevOrder.current !== order && containerRef.current) {
    snapshot.current = captureFlip(containerRef.current.querySelectorAll(itemSelector));
    prevOrder.current = order;
  }

  useLayoutEffect(() => {
    playFlip(snapshot.current, containerRef.current);
    snapshot.current = null;
  }, [order, containerRef]);
}
